import { PaymentProvidersById } from '../types';
import { GPIDeclineError, GPIErrorResponse, isValidGPIDeclineError, isValidGPIError } from '../types/gpiErrors';
import { PaymentProviderError } from './paymentProviderError';

interface createGPIErrorParams {
	rawError: string | unknown;
	code: string;
	clientType: 'PAYMENT' | 'REFUND' | 'TOKENIZER';
	notifyUser?: boolean;
	defaultErrorMessage?: string;
}

export const createGPIError = ({
	rawError,
	code,
	clientType,
	notifyUser = false,
	defaultErrorMessage,
}: createGPIErrorParams): PaymentProviderError => {
	let message: string;
	let isDeclineError = false;
	let isKnownDecline = false;
	const defaultPaymentErrorMessage =
		'Payment could not be processed. Please try again later or use a different payment method.';
	const defaultRefundErrorMessage = 'Refund could not be processed. Please try again later.';
	const defaultTokenizerErrorMessage =
		'Request could not be processed. Please try again later or use a different payment method.';
	if (!defaultErrorMessage) {
		defaultErrorMessage =
			clientType === 'PAYMENT'
				? defaultPaymentErrorMessage
				: clientType === 'REFUND'
					? defaultRefundErrorMessage
					: defaultTokenizerErrorMessage;
	}
	const defaultDeclineErrorMessage = 'Payment declined. Please try again or use a different payment method.';

	try {
		const parsedError = typeof rawError === 'string' ? JSON.parse(rawError) : rawError;

		if (isValidGPIError(parsedError)) {
			message = getCustomGPIErrorMessage(parsedError);
		} else if (isValidGPIDeclineError(parsedError)) {
			const mappedDeclineMessage = getCustomDeclineErrorMessage(parsedError);
			isKnownDecline = Boolean(mappedDeclineMessage);
			message = mappedDeclineMessage || defaultDeclineErrorMessage;
			isDeclineError = true;
		} else {
			message = defaultErrorMessage;
		}
	} catch {
		message = defaultErrorMessage;
	}

	return new PaymentProviderError(code, message, PaymentProvidersById.GPI, {
		category: isDeclineError ? 'PAYMENT_DECLINE' : 'PROVIDER_ERROR',
		subcategory: isDeclineError
			? isKnownDecline
				? 'KNOWN_DECLINE'
				: 'UNSPECIFIED_DECLINE'
			: 'UNMAPPED_PROVIDER_ERROR',
		retryable: Number(code) >= 500,
		notifyUser,
		rawMessage: typeof rawError === 'string' ? rawError : JSON.stringify(rawError),
	});
};

const customErrorMessages: Record<string, string> = {
	authentication_failed: 'Server error. Please contact support.',
	invalid_card:
		'Invalid card number. Please check your card details and try again or use a different payment method.',
	'/payment/amount': 'Amount should be greater than zero.',
	'/card/temporary_token':
		'Invalid input data. Please check your card details and try again or use a different payment method.',
	'/card/card_security_code':
		'Invalid Security Code (CVV). Please check your card details and try again or use a different payment method.',
	'/card/card_expiration_date':
		'Invalid Expiration Date. Please check your card details and try again or use a different payment method.',
	'/card/card_number':
		'Invalid Card Number. Please check your card details and try again or use a different payment method.',
	'/card/expiry_year':
		'Invalid Expiration Date. Please check your card details and try again or use a different payment method.',
	'/card/expiry_month':
		'Invalid Expiration Date. Please check your card details and try again or use a different payment method.',
	'/customer/billing_address/line1': 'Invalid Billing Address',
	'/customer/billing_address/postal_code': 'Invalid Postal Code',
	'/customer/first_name': 'Invalid First Name',
	'/customer/last_name': 'Invalid Last Name',
	invalid_payment_token: 'Invalid payment token. Please retry with valid token or use different payment method.',
	invalid_temporary_token:
		'Invalid temporary token. Please retry with valid temporary_token or use different payment method.',
	invalid_transaction_referenced: 'Duplicate reference_id sent.',
	processing_forbidden:
		'This transaction has already been processed. Please check your transaction history before trying again.',
	server_error: 'Server error. Please contact support.',
	server_busy: 'Server is busy. Please retry the request.',
	service_unavailable: 'Service temporarily unavailable, Please try later or contact support',
};

function getCustomGPIErrorMessage(gpiError: GPIErrorResponse): string {
	const detailPaths = gpiError.error.detail?.map((d) => d.data_path) ?? [];
	const fallbackMessage = 'Server error. Please contact support.';

	let customMessage: string | undefined;
	for (const dataPath of detailPaths) {
		const detailMessage = customErrorMessages[dataPath];

		if (!detailMessage) {
			continue;
		}

		if (customMessage !== undefined) {
			customMessage += `; ${detailMessage}`;
		} else {
			customMessage = detailMessage;
		}
	}

	return customMessage ?? customErrorMessages[gpiError.error.code] ?? fallbackMessage;
}

const customDeclineErrorMessages: Record<string, string> = {
	'003INVLD MERCH ID': 'Card declined. Please try again or use a different payment method.',
	'004DECLINE DO NOT RETRY': 'Card reported lost or stolen. Please use a different payment method.',
	'004PIC UP': 'Card reported lost or stolen. Please use a different payment method.',
	'005ADDTNL AUTHN REQD': 'Additional Authentication Required. Please try again or use a different payment method.',
	'005DECLINE': 'Card declined. Please check your card details and try again or use a different payment method.',
	'005DECLINE TRY LATER': 'Card declined. Please call your bank or use a different payment method.',
	'005DO NOT TRY AGAIN': 'Card declined. Please call your bank or use a different payment method.',
	'007DECLINE-CV2 FAIL':
		'Security code (CVV) check failed. Please check your card details and try again or use a different payment method.',
	'014DECLINE DO NOT RETRY':
		'Incorrect card number. Please check your card details and try again or use a different payment method.',
	'014INVLD ACCT':
		'Incorrect card number. Please check your card details and try again or use a different payment method.',
	'015DECLINE DO NOT RETRY':
		'Incorrect card number. Please check your card details and try again or use a different payment method.',
	'046CLOSED ACCOUNT': 'Closed Account. Please use a different payment method.',
	'046DECLINE DO NOT RETRY': 'Closed Account. Please use a different payment method.',
	'051DECLINE RETRY LATER': 'Insufficient funds. Please use a different payment method.',
	'051RETRY AFTER 1 HOUR': 'Insufficient funds. Please use a different payment method.',
	'051RETRY AFTER 10 DAYS': 'Insufficient funds. Please use a different payment method.',
	'051RETRY AFTER 24 HOURS': 'Insufficient funds. Please use a different payment method.',
	'054INVLD EXP DATE':
		'Invalid Expiration Date or Expired Card. Please check the expiration date or use a different payment method.',
	'057TRAN NOT ALLOWED':
		'The card issuer does not allow this type of transaction. Please use a different payment method.',
	'059DECLINE RETRY LATER': 'Card declined. Please try again later or use a different payment method.',
	'05CDECLINE RETRY LATER': 'Card declined. Please try again later or use a different payment method.',
	'062DECLINE RETRY LATER': 'Restricted card. Please use a different payment method.',
	'065DECLINE RETRY LATER': 'Card has exceeded withdrawal limit. Please use a different payment method.',
	'072ACCT NOT ACTIVATED': 'Account not activated. Please contact your bank or use a different payment method.',
	'075DECLINE RETRY LATER':
		'Card declined. Too many incorrect PIN entries. Please try again later or use a different payment method.',
	'078DECLINE RETRY LATER': 'Card not activated. Please contact your bank or use a different payment method.',
	'091DECLINE RETRY LATER': 'Card issuer unavailable. Please try again later or use a different payment method.',
	'09GDECLINE RETRY LATER':
		'Card soft declined for security hold, unusual purchase location or exceeded daily limit. Please try again later or use a different payment method.',
	'121LIMIT EXCEEDED': 'Card has exceeded withdrawal limit. Please use a different payment method.',
};

export const getCustomDeclineErrorMessage = (rawError: GPIDeclineError): string | undefined => {
	return customDeclineErrorMessages[rawError.processor_response];
};
